import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  Grid3X3, Users, ArrowLeft, Star, TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SkillsMatrixPage = () => {
  const [matrix, setMatrix] = useState([]);
  const [skillNames, setSkillNames] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    skill_name: "",
    proficiency_level: "3"
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [matrixRes, empRes] = await Promise.all([
          axios.get(`${API}/admin/training/skills-matrix`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setMatrix(matrixRes.data.matrix || []);
        setSkillNames(matrixRes.data.skill_names || []);
        setEmployees(empRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/training/skills`, {
        ...formData,
        proficiency_level: parseInt(formData.proficiency_level),
        assessed_by: "Admin Assessment"
      }, { headers });
      toast.success("Skill assessment added");
      setShowForm(false);
      const res = await axios.get(`${API}/admin/training/skills-matrix`, { headers });
      setMatrix(res.data.matrix || []);
      setSkillNames(res.data.skill_names || []);
    } catch (error) {
      toast.error("Failed to add skill assessment");
    }
  };

  const getProficiencyColor = (level) => {
    const colors = {
      1: "bg-red-500/30 text-red-400",
      2: "bg-orange-500/30 text-orange-400",
      3: "bg-yellow-500/30 text-yellow-400",
      4: "bg-green-500/30 text-green-400",
      5: "bg-emerald-500/30 text-emerald-400"
    };
    return colors[level] || "bg-slate-500/30 text-slate-400";
  };

  const getProficiencyLabel = (level) => {
    const labels = { 1: "Novice", 2: "Basic", 3: "Intermediate", 4: "Advanced", 5: "Expert" };
    return labels[level] || "N/A";
  };

  const totalSkillAssessments = matrix.reduce((sum, m) => sum + Object.keys(m.skills || {}).length, 0);
  const avgProficiency = matrix.reduce((sum, m) => {
    const skills = Object.values(m.skills || {});
    return sum + (skills.length ? skills.reduce((s, v) => s + v, 0) / skills.length : 0);
  }, 0) / (matrix.length || 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Skills Matrix...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="skills-matrix-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Skills Matrix
            </h1>
            <p className="text-slate-400 text-sm">Track employee skill proficiency levels</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Star className="w-4 h-4 mr-2" /> Add Assessment
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{matrix.length}</p>
            <p className="text-xs text-slate-400">Employees Assessed</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Grid3X3 className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{skillNames.length}</p>
            <p className="text-xs text-slate-400">Skills Tracked</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Star className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{totalSkillAssessments}</p>
            <p className="text-xs text-slate-400">Total Assessments</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <TrendingUp className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-2xl font-bold text-purple-400">{avgProficiency.toFixed(1)}</p>
            <p className="text-xs text-slate-400">Avg Proficiency</p>
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card className="glass-card mb-6">
        <CardContent className="p-4">
          <p className="text-sm text-slate-400 mb-2">Proficiency Levels:</p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map(level => (
              <Badge key={level} className={getProficiencyColor(level)}>
                {level} - {getProficiencyLabel(level)}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Skill Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">Add Skill Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Employee</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Skill Name</label>
                  <Input
                    value={formData.skill_name}
                    onChange={(e) => setFormData({ ...formData, skill_name: e.target.value })}
                    className="input-sovereign"
                    placeholder="e.g., Project Management, Data Analysis"
                    list="skill-suggestions"
                    required
                  />
                  <datalist id="skill-suggestions">
                    {skillNames.map(skill => (
                      <option key={skill} value={skill} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Proficiency Level</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.proficiency_level}
                    onChange={(e) => setFormData({ ...formData, proficiency_level: e.target.value })}
                  >
                    <option value="1">1 - Novice</option>
                    <option value="2">2 - Basic</option>
                    <option value="3">3 - Intermediate</option>
                    <option value="4">4 - Advanced</option>
                    <option value="5">5 - Expert</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Add</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Skills Matrix Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Employee Skills Matrix</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-slate-900 z-10">Employee</th>
                  <th className="sticky left-0 bg-slate-900 z-10">Department</th>
                  {skillNames.slice(0, 8).map(skill => (
                    <th key={skill} className="text-center whitespace-nowrap">{skill}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => (
                  <tr key={row.employee_id}>
                    <td className="sticky left-0 bg-slate-900 text-white font-medium z-10">{row.employee_name}</td>
                    <td className="sticky left-0 bg-slate-900 text-slate-400 z-10">{row.department}</td>
                    {skillNames.slice(0, 8).map(skill => {
                      const level = row.skills?.[skill];
                      return (
                        <td key={skill} className="text-center">
                          {level ? (
                            <Badge className={`${getProficiencyColor(level)} w-8 justify-center`}>
                              {level}
                            </Badge>
                          ) : (
                            <span className="text-slate-600">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SkillsMatrixPage;
